import { WebPubSubClient } from '@azure/web-pubsub-client';
import { createContext, FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { IS_ENCRYPTION_ENABLED } from 'src/config';
import {
  ChatStep,
  createChatStepsUpdater,
  getFormattedStreamingChatMessageSteps
} from 'src/content/Chat/Conversation/ChatSteps/utils';
import useImageGenerationChat, {
  ImageGenerationChat
} from 'src/hooks/chat/useImageGenerationChat';
import { getEncryptParams } from 'src/keycloak';
import { useDispatch, useSelector } from 'src/redux/store';
import { authSignIn } from 'src/redux/slices/auth';
import { decryptResponse } from 'src/services/utils';
import { StringKeys } from 'src/types/base';
import {
  CHAT_STEP_MODE,
  CHAT_STEP_NAME,
  PubsubEvents,
  TOOLS_LIST
} from 'src/types/enum';
import { MAX_RETRIES_PUBSUB } from 'src/utils/constants';
import logger from 'src/utils/logger';
import { isImageGenMessage } from 'src/utils/utils';

type AzureWebPubSubChatContextType = {
  connectionId: string;
  streamMessage: string;
  chatMeta?: any;
  chatSteps?: any;
  error?: any;
  resetContext: () => void;
  chatStepStreamMessage?: string;
  isExecutingPlan?: boolean;
  preCanvasStream?: string;
  postCanvasStream?: string;
  isFindAndReplaceCanvasMode?: boolean;
  actionState?: StringKeys;
  setIsFindAndReplaceCanvasMode?: (flag: boolean) => void;
  appendGeneratedImageResources: (
    rid: number
  ) => { public_url: string; format: string; display_name: string }[];
  imageGenerationChat: ImageGenerationChat;
  reconnectPubSub: () => void;
};

export const AzureWebPubSubChatContext =
  createContext<AzureWebPubSubChatContextType>(
    {} as AzureWebPubSubChatContextType
  );

export const AzureWebPubSubChatProvider: FC = ({ children }) => {
  const { azureWebPubSubInfo } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { id } = useParams();

  const [connectionId, setConnectionId] = useState<string>();
  const wpsClientRef = useRef<WebPubSubClient | null>(null);
  const [streamMessage, setStreamMessage] = useState<string>('');
  const [preCanvasStream, setPreCanvasStream] = useState<string>('');
  const [postCanvasStream, setPostCanvasStream] = useState<string>('');
  const [isFindAndReplaceCanvasMode, setIsFindAndReplaceCanvasMode] =
    useState(false);

  const [chatMeta, setChatMeta] = useState<any>();
  const [chatSteps, setChatSteps] = useState<ChatStep[]>([]);
  const [chatStepStreamMessage, setChatStepStreamMessage] = useState<string>();
  const [error, setError] = useState<any>();
  const [isExecutingPlan, setIsExecutingPlan] = useState<boolean>(false);
  const [actionState, setActionState] = useState(null);

  const isMounted = useRef(true);
  const generatedImageResourcesRef = useRef<
    Record<
      number,
      { public_url: string; format: string; display_name: string }[]
    >
  >({});
  const planStepsCount = useRef(0);
  const messageBufferRef = useRef<string[]>([]);
  const animationFrameRef = useRef<number | null>(null);
  const streamingStepsRef = useRef<Set<number>>(new Set());
  const codeExecutorMessageTaskIdRef = useRef<string | undefined>(undefined);

  const chatStepManagerRef =
    useRef<ReturnType<typeof createChatStepsUpdater>>();

  const imageGenerationChat = useImageGenerationChat();

  useEffect(() => {
    isMounted.current = true;
    generatedImageResourcesRef.current = {};

    const wpsClient = new WebPubSubClient(azureWebPubSubInfo.url);
    wpsClientRef.current = wpsClient;

    wpsClient.on('connected', (e) => {
      if (isMounted.current) {
        logger.log('connected chat wps connection', e.connectionId);
        setConnectionId(e.connectionId);
      }
    });

    wpsClient.on('disconnected', (e) => {
      logger.log('disconnected chat wps connection', e.connectionId);
      if (isMounted.current) {
        setConnectionId(undefined);
        resetContext();
      }
    });

    wpsClient.on('server-message', ({ message }: any) => {
      const decryptKey = getEncryptParams()?.k;
      let data = decryptResponse(message, decryptKey);
      if (!IS_ENCRYPTION_ENABLED) {
        data = JSON.parse(data);
      }
      handleIncomingMessage(data);
    });

    wpsClient.start().catch((e) => {
      logger.error('Initial WPS connection failed', e);
      if (isMounted.current) dispatch(authSignIn());
    });

    const handleOnline = () => {
      if (!isMounted.current) return;
      logger.log('Network restored — reconnecting WPS...');
      wpsClientRef.current?.start().catch((e) => {
        logger.error('WPS reconnect on network restore failed', e);
        if (isMounted.current) dispatch(authSignIn());
      });
    };

    window.addEventListener('online', handleOnline);

    return () => {
      isMounted.current = false;
      window.removeEventListener('online', handleOnline);
      wpsClient?.stop();
    };
  }, [id, azureWebPubSubInfo.url]);

  const reconnectPubSub = async () => {
    for (let attempt = 1; attempt <= MAX_RETRIES_PUBSUB; attempt++) {
      try {
        logger.log(
          `PubSub reconnect attempt ${attempt}/${MAX_RETRIES_PUBSUB}...`
        );

        await wpsClientRef.current?.start();

        logger.log('Successfully reconnected to PubSub');
        return true;
      } catch (e) {
        logger.error(`Reconnect attempt ${attempt} failed`, e);
      }
    }

    logger.error('All PubSub reconnect attempts failed');
    return false;
  };

  const handleIncomingMessage = (data: any) => {
    switch (data?.event) {
      case PubsubEvents.STREAM:
        handleStreamEvent(data);
        break;

      case PubsubEvents.CHAT_META:
        handleChatMetaEvent(data);
        break;

      case PubsubEvents.CHAT_STEP:
        handleChatStepEvent(data);
        break;

      case PubsubEvents.ERROR:
        setError(data);
        break;

      default:
        break;
    }
  };

  const handleStreamEvent = (data: any) => {
    if (
      !planStepsCount.current &&
      data?.task_id !== '0' &&
      !codeExecutorMessageTaskIdRef.current
    ) {
      codeExecutorMessageTaskIdRef.current = data?.task_id;
    }

    if (planStepsCount.current && data?.task_id < planStepsCount.current) {
      if (!streamingStepsRef.current.has(data.task_id)) {
        chatStepManagerRef.current?.update(data.task_id, {
          content: { meta: { isStreaming: true } }
        });
        streamingStepsRef.current.add(data.task_id);
      }
      setChatStepStreamMessage(data.response);
      const updated = chatStepManagerRef.current?.appendToResponse(
        data?.task_id,
        data?.response
      );

      if (updated?.length > 0) setChatSteps((prev) => updated);
    } else {
      messageBufferRef.current.push(data.response);

      if (document.hidden) {
        updateStreamMessage();
      } else if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(updateStreamMessage);
      }
    }
  };

  const handleChatStepEvent = (data: any) => {
    // Check for image generation start
    if (
      isImageGenMessage(data?.tool_name) &&
      data?.step_name === CHAT_STEP_NAME.TOOL_INPUT
    ) {
      imageGenerationChat.setIsGeneratingImage(true);
    }

    // Check for image generation response based on step_name and tool_name
    const isImageGenerationResponse =
      data?.step_name === CHAT_STEP_NAME.TOOL_END &&
      isImageGenMessage(data?.tool_name);

    if (isImageGenerationResponse && data?.rid !== undefined) {
      const content = Array.isArray(data.content) ? data.content : [];
      const imageUrl =
        content.find((item) => item?.type === 'image_url')?.image_url?.url ??
        null;
      if (isMounted.current && imageUrl) {
        const resource = {
          public_url: imageUrl,
          format: 'png',
          display_name: 'generated-image'
        };
        // Add the generated image to the image resources so that once we update message after chat meta event,
        // we can append these image resources to that message
        const existing = generatedImageResourcesRef.current[data.rid] || [];
        generatedImageResourcesRef.current[data.rid] = [...existing, resource];
        imageGenerationChat.setIsGeneratingImage(false);
      }
      return;
    }
    // canvas mode steps are handled below
    if (
      data?.mode === CHAT_STEP_MODE.EDIT_IMPLIED ||
      data?.mode === CHAT_STEP_MODE.RESPOND_SELECTION
    ) {
      setPreCanvasStream(data?.pre_select);
      setPostCanvasStream(data?.post_select);
      return;
    }
    if (data?.mode === CHAT_STEP_MODE.FIND_AND_REPLACE) {
      setIsFindAndReplaceCanvasMode(true);
    }
    // canvas mode complete

    // Planner steps are handled below
    if (
      data?.tool_name === TOOLS_LIST.PLANNER &&
      data?.step_name === CHAT_STEP_NAME.PLAN
    ) {
      let plan = { task_list: [] };
      try {
        plan = JSON.parse(data?.response);
      } catch (error) {
        logger.error('Error parsing plan data', error);
      }
      planStepsCount.current = plan?.task_list?.length;
    }
    if (
      data?.tool_name === TOOLS_LIST.PLANNER &&
      data?.step_name === CHAT_STEP_NAME.ROUTE_SELECTED
    ) {
      setIsExecutingPlan(true);
    }
    setChatSteps((prev) => {
      const formattedSteps = getFormattedStreamingChatMessageSteps(
        data,
        planStepsCount.current
      );

      // Dedupe by taskId. Backend emits both TOOL_SELECTED and TOOL_INPUT for
      // the same task; without this guard we'd append two steps with the same
      // taskId, and the taskId→index map (last-write-wins) would route all
      // subsequent STREAM chunks to the duplicate — splitting one task's text
      // across two visible steps.
      const existingTaskIds = new Set(prev.map((step) => step.taskId));
      const stepsToAdd = formattedSteps.filter(
        (step) => step.taskId == null || !existingTaskIds.has(step.taskId)
      );

      if (stepsToAdd.length === 0) return prev;

      const newSteps = [...prev, ...stepsToAdd];

      chatStepManagerRef.current = createChatStepsUpdater(newSteps);
      chatStepManagerRef.current?.rebuild();
      return newSteps;
    });
    if (codeExecutorMessageTaskIdRef.current) {
      setStreamMessage((prev) => {
        if (prev.length > 0) {
          let response = data?.response;
          try {
            response = JSON.parse(response);
          } catch (error) {
            response = {};
          }
          let codeSnippet = response?.args?.code_snippet;
          let language = response?.args?.language || 'python';
          chatStepManagerRef.current?.rebuild();
          const updated = chatStepManagerRef.current?.update(
            codeExecutorMessageTaskIdRef.current,
            {
              content: {
                response: `${prev || ''}${
                  codeSnippet
                    ? `\n\`\`\`${language}\n${codeSnippet}\n\`\`\``
                    : ''
                }`
              }
            }
          );
          if (updated?.length > 0) setChatSteps((prev) => updated);
          codeExecutorMessageTaskIdRef.current = undefined;
          return '';
        }
        return prev;
      });
    }
  };

  const handleChatMetaEvent = (data: any) => {
    if (planStepsCount.current && data?.task_id < planStepsCount.current) {
      const updated = chatStepManagerRef.current?.update(data?.task_id, {
        content: { meta: { isStreaming: false, files: data?.files } }
      });
      streamingStepsRef.current.delete(data.task_id);

      if (updated?.length > 0) setChatSteps((prev) => updated);
    }
    if (data?.step_name === CHAT_STEP_NAME.FINAL) {
      if (isMounted.current) {
        setChatMeta(data);
      }

      setStreamMessage((prevStream) => {
        const hasMainResponse = prevStream.length > 0;
        if (!hasMainResponse) {
          setChatSteps((prevSteps) => {
            if (prevSteps.length === 0) return prevSteps;
            const lastTaskId = prevSteps[prevSteps.length - 1]?.taskId;
            if (!lastTaskId) return prevSteps;
            const updated = chatStepManagerRef.current?.update(lastTaskId, {
              content: { meta: { isStreaming: true } }
            });
            return updated?.length > 0 ? [...updated] : prevSteps;
          });
        }
        return prevStream;
      });
    }
    if (data?.step_name === CHAT_STEP_NAME.INTERRUPT) {
      // added space in case of interrupt, so that we could escape the null/undefined stream message checks
      messageBufferRef.current.push(' ');
      updateStreamMessage();
      setActionState(data);
      setChatMeta(data);
    }
  };
  const updateStreamMessage = () => {
    if (messageBufferRef.current.length > 0) {
      const pendingMessage = messageBufferRef.current.join('');
      setStreamMessage((prev) => prev + pendingMessage);
      messageBufferRef.current = [];
    }
    animationFrameRef.current = null;
  };

  const resetContext = () => {
    if (isMounted.current) {
      setStreamMessage('');
      setPreCanvasStream('');
      setPostCanvasStream('');
      setChatStepStreamMessage('');
      setChatMeta(undefined);
      setError(undefined);
      setChatSteps([]);
      streamingStepsRef.current.clear();
      chatStepManagerRef.current = undefined;
      setIsExecutingPlan(false);
      imageGenerationChat.setIsGeneratingImage(false);
      planStepsCount.current = undefined;
      setActionState(null);
      generatedImageResourcesRef.current = {};
      codeExecutorMessageTaskIdRef.current = undefined;
    }
  };

  const appendGeneratedImageResources = (rid: number) => {
    const resources = generatedImageResourcesRef.current[rid] || [];
    delete generatedImageResourcesRef.current[rid];
    return resources;
  };

  return (
    <AzureWebPubSubChatContext.Provider
      value={{
        connectionId,
        streamMessage,
        resetContext,
        chatMeta,
        chatSteps,
        error,
        chatStepStreamMessage,
        isExecutingPlan,
        preCanvasStream,
        postCanvasStream,
        isFindAndReplaceCanvasMode,
        setIsFindAndReplaceCanvasMode,
        appendGeneratedImageResources,
        imageGenerationChat,
        actionState,
        reconnectPubSub
      }}
    >
      {children}
    </AzureWebPubSubChatContext.Provider>
  );
};
